import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Image, Spin } from 'antd'
import { CameraOutlined, EyeInvisibleOutlined } from '@ant-design/icons'

import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { PHOTO_URL } from '../utils/apiutils'
import {
  listServicePhotosByBooking,
  listServicePhotosByBookings
} from '../api/servicePhoto'

/**
 * 結帳紀錄／結帳信息裡的「施作照片」小縮圖列。
 *
 * 跟 ServicePhotoBookingSection（預約詳情的完整管理區塊）分工：
 * 那邊能上傳、切換可見、刪除；這邊只給看 —— 店家翻結帳紀錄時
 * 要的是「這次做了什麼」的一眼確認，不是管理介面。要改照片就回預約詳情。
 *
 * 兩種用法：
 * 1. 列表頁（結帳紀錄）：父層用批次 API 撈好，直接把 photos / total 傳進來，
 *    元件不再自己打 API —— 一頁十筆各自請求會變成十個往返。
 * 2. 單筆（顧客詳情的結帳 Modal）：只給 bookingId，元件自己去撈。
 *
 * 方案與權限自己判斷，父層完全不用管，沒開通就 return null。
 */

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const Thumb = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e5e7;
  background: #f5f5f7;
  flex: none;

  .ant-image,
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    cursor: zoom-in;
  }
`

/* 未對客人公開的那幾張要一眼分得出來，不然店家會以為客人都看得到 */
const HiddenBadge = styled.span`
  position: absolute;
  right: 2px;
  bottom: 2px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 11px;
  pointer-events: none;
`

const MoreTile = styled.button`
  width: 64px;
  height: 64px;
  flex: none;
  border-radius: 8px;
  border: 1px dashed #d9d9d9;
  background: #fafafa;
  color: #86868b;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #d5728a;
    color: #d5728a;
  }
`

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #86868b;
  margin-bottom: 8px;

  .anticon {
    color: #d5728a;
  }
`

export const toServicePhotoUrl = (path) => {
  if (!path) return ''
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  return `${PHOTO_URL}${path}`
}

const ServicePhotoThumbs = ({
  businessId,
  bookingId,
  photos: photosProp,
  total: totalProp,
  showLabel = true,
  size
}) => {
  const { hasFeature } = useMembership()
  const { canAny } = usePermission()

  const featureEnabled = hasFeature('servicePhoto')
  const canView = canAny(['servicePhoto:view', 'servicePhoto:manage'])
  const allowed = featureEnabled && canView

  // photosProp 有給就是列表模式（父層批次撈好），沒給才自己去撈
  const controlled = Array.isArray(photosProp)

  const [photos, setPhotos] = useState(controlled ? photosProp : [])
  const [total, setTotal] = useState(
    controlled ? (totalProp ?? photosProp.length) : 0
  )
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!controlled) return
    setPhotos(photosProp)
    setTotal(totalProp ?? photosProp.length)
  }, [controlled, photosProp, totalProp])

  useEffect(() => {
    if (controlled || !allowed || !businessId || !bookingId) return
    let alive = true
    setLoading(true)
    listServicePhotosByBooking(businessId, bookingId)
      .then((res) => {
        if (!alive) return
        const list = res?.data?.data || []
        setPhotos(list)
        setTotal(list.length)
      })
      .catch(() => {
        // 靜默：方案／權限問題不該在結帳紀錄跳錯誤
        if (alive) {
          setPhotos([])
          setTotal(0)
        }
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [controlled, allowed, businessId, bookingId])

  /**
   * 列表模式只拿到前幾張，點「+N」時才把整筆補齊。
   * 補齊後 antd 的 PreviewGroup 就能左右翻完全部，不用跳去預約詳情。
   */
  const loadAll = useCallback(async () => {
    if (!businessId || !bookingId) return
    setLoading(true)
    try {
      const res = await listServicePhotosByBookings(
        businessId,
        [bookingId],
        // 刻意要 20：上傳上限雖然是 8 張，但改上限前的舊預約可能有更多張，要補得齊
        20
      )
      const entry = res?.data?.data?.[bookingId]
      if (entry?.photos?.length) {
        setPhotos(entry.photos)
        setTotal(entry.total ?? entry.photos.length)
      }
    } catch (err) {
      // 補不到就維持原本那幾張，不打擾店家
    } finally {
      setLoading(false)
    }
  }, [businessId, bookingId])

  if (!allowed) return null
  if (loading && photos.length === 0) return <Spin size='small' />
  if (photos.length === 0) return null

  const hiddenCount = photos.filter((p) => p.visibleToCustomer === false).length
  const remaining = Math.max(total - photos.length, 0)
  const thumbStyle = size ? { width: size, height: size } : undefined

  return (
    <div>
      {showLabel && (
        <Label>
          <CameraOutlined />
          施作照片 {total} 張
          {hiddenCount > 0 && (
            <span style={{ color: '#b0b0b5' }}>
              （{hiddenCount} 張未對客人公開）
            </span>
          )}
        </Label>
      )}
      <Wrapper>
        <Image.PreviewGroup>
          {photos.map((photo) => (
            <Thumb key={photo._id} style={thumbStyle}>
              <Image
                src={toServicePhotoUrl(photo.previewUrl || photo.url)}
                preview={{ src: toServicePhotoUrl(photo.url) }}
                alt={photo.caption || '施作照片'}
              />
              {/* pointer-events 關掉，點在角標上一樣要能開預覽 */}
              {photo.visibleToCustomer === false && (
                <HiddenBadge>
                  <EyeInvisibleOutlined />
                </HiddenBadge>
              )}
            </Thumb>
          ))}
        </Image.PreviewGroup>
        {remaining > 0 && (
          <MoreTile
            type='button'
            style={thumbStyle}
            onClick={loadAll}
            disabled={loading}
          >
            {loading ? <Spin size='small' /> : `+${remaining}`}
          </MoreTile>
        )}
      </Wrapper>
    </div>
  )
}

export default ServicePhotoThumbs
